import type { CombatStartPlan, PostedVerdict, ContingencyPlan } from './start-roll';
import type { CombatJoinPlan } from './join-plan';
import type { CombatExitPlan } from './exit-plan';
import type { RefillOp } from '../charges/combat-refill';
import { applyTurnTick } from '../turns/tick-frame';
import { statusRows, removeStatusFromRow } from '../statuses/expiry';
import { encounterCreatureField, statusIdOf } from '../../shared/array-types';

type Row = Record<string, unknown>;
export interface CombatApplyPlan {
    action: string; start?: CombatStartPlan; join?: CombatJoinPlan; exit?: CombatExitPlan;
    start_rest?: Array<{ id: string; aps?: number; reaction?: number }>;
    charge_pre?: RefillOp[]; refill?: RefillOp[];
    nav?: { pointer: string; round: number; phase_reset: boolean };
}

/** Project only values already decided by the combat planners. No rules or dice
 * run here. Actor and observer use the same scalar/status frame afterwards. */
export function applyCombatPlan(encounter: Row, command: CombatApplyPlan, definition: (id: string) => Row | null): {
    encounter: Row; rows: Record<string, { ec: Row; status_remove_ids: string[]; statuses_upsert: Row[]; charges: Array<{ item_id: string; current: number }> }>;
} {
    const roster = encounter.encounter_creatures_arr as Row[];
    const idOf = (row: Row): string => String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase();
    const members = new Map(roster.map(row => [idOf(row), row]));
    const before = new Map(roster.map(row => [idOf(row), new Set(statusRows(row, 'target').map(s => statusIdOf(s).toUpperCase()))]));
    const touched = new Set<string>(), pools = new Map<string, Map<string, number>>();
    const write = (id: string, values: Row, bump = false): void => {
        const row = members.get(id); if (!row) return;
        const ec = row.encounter_creatures as Row;
        const rev = Number(encounterCreatureField(ec, 'movement_rev') ?? 0);
        Object.assign(ec, values); if (bump) ec.movement_rev = rev + 1;
        if ('aps' in values) ec.aps_used = Math.max(0, Number(encounterCreatureField(ec, 'aps_total') ?? 3) - Number(values.aps)) || null;
        touched.add(id);
    };
    const remove = (id: string): void => { for (const row of roster) {
        if (statusRows(row, 'target').some(s => statusIdOf(s).toUpperCase() === id)
            || statusRows(row, 'caster').some(s => statusIdOf(s).toUpperCase() === id)) touched.add(idOf(row));
        removeStatusFromRow(row, id);
    } };
    const verdict = (v: PostedVerdict): void => {
        for (const id of applyTurnTick(roster, { ...v, statusesChanged: v.statusStacks ?? [] }, definition, encounter)) touched.add(id);
        // Each saved verdict advances every EC row it writes, independently of
        // the combat member reset and the first-turn reset.
        for (const id of Object.keys(v.ec)) write(id, {}, true);
        for (const p of v.playerCharactersChanged) {
            const row = members.get(p.item_id); if (row?.player_characters) (row.player_characters as Row)[p.column] = p.after;
        }
        for (const c of v.charges) { const held = pools.get(c.holder) ?? new Map(); held.set(c.item_id, c.charges); pools.set(c.holder, held); }
    };
    const charges = (ops: RefillOp[]): void => verdict({ rows: [], ec: {}, statusesAdded: [], statusesRemoved: [], statusStacks: [], playerCharactersChanged: [],
        charges: ops.map(op => ({ holder: op.holder, item_id: op.item_id, before: 0, charges: op.to })) });
    const contingencies = (plan: ContingencyPlan): void => { for (const step of plan.steps) {
        remove(step.status_id);
        if (step.mana) write(step.owner_id, { mana: step.mana.after });
        if (step.kind === 'fire') verdict(step.verdict);
    } };
    let scalars: Row = {};
    if (command.start) {
        const p = command.start, participants = new Set(p.participants.map(member => member.id));
        for (const row of roster) write(idOf(row), { combat_id: participants.has(idOf(row)) ? p.combat_id : null });
        for (const rest of command.start_rest ?? []) if (participants.has(rest.id)) {
            const { id, ...values } = rest; write(id, { ...values, ...('aps' in values ? { aps_used: null } : {}) });
        }
        charges(command.charge_pre ?? []);
        for (const entry of p.begins.verdicts) verdict(entry.verdict);
        for (const roll of p.initiative.rolls) {
            verdict(roll.verdict);
            for (const id of roll.resting?.remove ?? []) remove(id);
            if (roll.resting?.pause) {
                const pause = roll.resting.pause;
                for (const row of roster) for (const mode of ['target', 'caster'] as const) for (const status of statusRows(row, mode))
                    if (statusIdOf(status).toUpperCase() === pause.status_id) {
                        for (const [prefix, clock] of [['status_ends_campaign_', pause.ends], ['status_paused_campaign_', pause.paused]])
                            ['year', 'month', 'day', 'hour', 'minute', 'second'].forEach((unit, index) => { status[prefix + unit] = Number(clock!.split('-')[index] ?? 0); });
                        touched.add(idOf(row));
                    }
            }
        }
        for (const { id, ...values } of p.members) write(id, { ...values, aps_used: null }, true);
        charges(command.refill ?? []);
        contingencies(p.contingencies);
        scalars = { ...p.encounter, active_combat_id: p.combat_id, turn_phase: 'main', pending_main_turn_encounter_creature_id: '', legendary_turn_queue: '' };
        const { id, legacy: _legacy, removals, tick, ...values } = p.first;
        write(id, { ...values, aps_used: null }, true);
        for (const removal of removals) remove(removal.status_id);
        if (tick) verdict(tick);
    } else if (command.join) {
        for (const { id, reason: _reason, before: _before, ...values } of command.join.joiners)
            write(id, { ...values, aps_used: null, combat_id: command.join.combat_id }, true);
    } else if (command.exit) {
        contingencies(command.exit.contingencies);
        scalars = { ...command.exit.encounter, active_combat_id: '' };
        for (const { id, ...values } of command.exit.members) write(id, { ...values, combat_id: null });
        for (const removal of command.exit.status_removals) remove(removal.status_id);
        charges(command.exit.charges);
    } else if (command.nav) {
        scalars = { current_encounter_creature_id_turn: command.nav.pointer, current_round: command.nav.round,
            ...(command.nav.phase_reset ? { turn_phase: 'main', pending_main_turn_encounter_creature_id: '', legendary_turn_queue: '' } : {}) };
    } else charges(command.refill ?? []);
    const rows = Object.fromEntries([...touched].map(id => {
        const row = members.get(id)!;
        const target = statusRows(row, 'target');
        const held = new Set(target.map(s => statusIdOf(s).toUpperCase()));
        const ec: Row = { ...row.encounter_creatures as Row };
        for (const key of ['aps', 'aps_used', 'weapon_attack', 'head_attack', 'luck', 'reaction', 'movement_pct', 'movement_rev', 'creature_initiative'])
            ec[key] = Number(encounterCreatureField(row.encounter_creatures, key) ?? 0);
        const total = encounterCreatureField(row.encounter_creatures, 'aps_total');
        if (total != null) ec.aps_total = Number(total); else delete ec.aps_total;
        return [id, { ec, status_remove_ids: [...before.get(id) ?? []].filter(id => !held.has(id)),
            statuses_upsert: [...target], charges: [...pools.get(id) ?? []].map(([item_id, current]) => ({ item_id, current })) }];
    }));
    return { encounter: scalars, rows };
}
